/** Meta Pixel (Facebook Pixel) helper utilities */

/**
 * Master kill-switch for direct in-app Meta Pixel tracking.
 *
 * Deactivated: the Meta Pixel is now loaded and fired through Google Tag
 * Manager, so the app no longer talks to `fbq` directly. While this is
 * `false`, the `MetaPixel` loader is a no-op and every `track*()` helper
 * below returns without firing — the call sites stay in place so tracking
 * can be re-enabled by flipping this single flag back to `true`.
 */
export const PIXEL_ENABLED: boolean = false;

const META_PIXEL_ID = "937015625323240";

declare global {
  interface Window {
    fbq?: (
      action: "init" | "track" | "trackCustom" | "consent",
      eventOrId: string,
      params?: Record<string, string | number | boolean>
    ) => void;
    _fbq?: Window["fbq"];
  }
}

function fbq(
  action: "track" | "trackCustom",
  event: string,
  params?: Record<string, string | number | boolean>
) {
  // Deactivated — Meta Pixel tracking is handled via GTM. See PIXEL_ENABLED.
  if (!PIXEL_ENABLED) return;
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq(action, event, params ?? {});
  }
}

/** Track a lead generation event (e.g. clicking a register link) */
export function trackLead(params?: Record<string, string | number | boolean>) {
  fbq("track", "Lead", params);
}

/** Track a completed registration / booking confirmation */
export function trackCompleteRegistration(params?: Record<string, string | number | boolean>) {
  fbq("track", "CompleteRegistration", params);
}

/** Track a contact action (e.g. clicking mailto) */
export function trackContact(params?: Record<string, string | number | boolean>) {
  fbq("track", "Contact", params);
}

/** Track a content view */
export function trackViewContent(params?: Record<string, string | number | boolean>) {
  fbq("track", "ViewContent", params);
}

/** Track clicking a "start / try for free" CTA that leads to sign-up */
export function trackInitiateCheckout(params?: Record<string, string | number | boolean>) {
  fbq("track", "InitiateCheckout", params);
}

/** Track a demo / meeting booking click */
export function trackSchedule(params?: Record<string, string | number | boolean>) {
  fbq("trackCustom", "Schedule", params);
}

/** Track "Try Now for Free" clicks */
export function trackTryNowButtonClick(params?: Record<string, string | number | boolean>) {
  fbq("trackCustom", "TryNowButtonClick", params);
}

/** Track "Book a Meeting to Find Out More" clicks */
export function trackBookMeetingButtonClick(params?: Record<string, string | number | boolean>) {
  fbq("trackCustom", "BookMeetingButtonClick", params);
}

/** Track a successful lead-form submission (e.g. /anfrage) */
export function trackFormSubmit(params?: Record<string, string | number | boolean>) {
  fbq("trackCustom", "FormSubmit", params);
}

/** Track demo video play */
export function trackVideoPlayed(params?: Record<string, string | number | boolean>) {
  fbq("trackCustom", "VideoPlayed", params);
}

export { META_PIXEL_ID };
